import { useState, useEffect, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import {
  LanguageContext,
  type Language,
  type LanguageContextType,
} from "@/shared/lib/language";

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { i18n: i18nInstance } = useTranslation();
  // Boot precedence (highest → lowest):
  //   1. URL override: ?lng=ne  (querystring detector in i18n.ts)
  //   2. Persisted choice: localStorage["storemandu-language"]
  //   3. Browser language: navigator.language (browser detector in i18n.ts)
  //   4. Fallback: "en"
  //
  // i18next's LanguageDetector handles 1, 3, 4. We read i18n.language
  // after init so this provider reflects what the detector resolved.
  // We also fall back to "storemandu-language" if it was set by a
  // previous build, so existing users keep their choice.
  const [language, setLanguageState] = useState<Language>(() => {
    const resolved = (i18n.language?.split("-")[0] as Language) || "en";
    // Legacy: prefer storemandu-language if it was set by a previous build.
    const legacy = localStorage.getItem("storemandu-language") as Language | null;
    return (legacy && ["en", "ne", "hi"].includes(legacy) ? legacy : resolved) as Language;
  });

  useEffect(() => {
    // Initialize i18n with saved language
    const initLanguage = async () => {
      if (i18nInstance.language !== language) {
        await i18nInstance.changeLanguage(language);
      }
    };
    initLanguage();
  }, [language, i18nInstance]);

  useEffect(() => {
    // Save to localStorage
    localStorage.setItem("storemandu-language", language);

    // Update HTML lang attribute for accessibility
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = async (lang: Language) => {
    setLanguageState(lang);
    await i18nInstance.changeLanguage(lang);
  };

  // Use i18n's translation function
  const t = (key: string, options?: any): string => {
    const result = i18nInstance.t(key, options);
    return typeof result === "string" ? result : String(result);
  };

  const value: LanguageContextType = { language, setLanguage, t, i18n: i18nInstance };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}
